import { useEffect, useMemo, useRef, useState } from 'react';
import { LuCircleCheck } from 'react-icons/lu';
import { useStrings } from '../tenant/TenantProvider';
import { cx } from '../ui/cx';

const SNIPPETS = [
  {
    file: 'two_sum.py',
    language: 'python',
    code: `def two_sum(nums, target):
    seen = {}
    for i, n in enumerate(nums):
        if target - n in seen:
            return [seen[target - n], i]
        seen[n] = i
    return []`,
  },
  {
    file: 'valid_parentheses.cpp',
    language: 'cpp',
    code: `bool isValid(string s) {
    stack<char> open;
    for (char c : s) {
        if (c == '(') open.push(')');
        else if (c == '[') open.push(']');
        else if (c == '{') open.push('}');
        else if (open.empty() || open.top() != c) return false;
        else open.pop();
    }
    return open.empty();
}`,
  },
  {
    file: 'max_subarray.js',
    language: 'javascript',
    code: `function maxSubArray(nums) {
  let best = nums[0];
  let run = 0;
  for (const n of nums) {
    run = Math.max(n, run + n);
    best = Math.max(best, run);
  }
  return best;
}`,
  },
];

const KEYWORDS = {
  python: ['def', 'for', 'in', 'if', 'return', 'else', 'elif', 'while', 'not', 'and', 'or'],
  cpp: ['for', 'if', 'else', 'return', 'while', 'true', 'false'],
  javascript: ['function', 'let', 'const', 'for', 'of', 'return', 'if', 'else'],
};
const TYPES = { cpp: ['bool', 'string', 'char', 'int', 'stack', 'vector'], python: [], javascript: ['Math'] };
const TOKEN = /(\s+)|('(?:\\.|[^'\\])*'|"(?:\\.|[^"\\])*")|(\d+)|([A-Za-z_]\w*)|(.)/g;
const TYPE_INTERVAL_MS = 16;
const JUDGE_MS = 1500;
const ACCEPTED_MS = 2800;
const CLEAR_MS = 450;
const MAX_LINES = Math.max(...SNIPPETS.map((snippet) => snippet.code.split('\n').length));

function tokenize(code, language) {
  return code.split('\n').map((line) => {
    const tokens = [];
    let match;
    TOKEN.lastIndex = 0;
    while ((match = TOKEN.exec(line)) !== null) {
      const [text, space, string, number, word] = match;
      let kind = '';
      if (space) kind = '';
      else if (string) kind = 'str';
      else if (number) kind = 'num';
      else if (word) {
        const rest = line.slice(TOKEN.lastIndex).trimStart();
        if (KEYWORDS[language].includes(word)) kind = 'kw';
        else if (TYPES[language].includes(word)) kind = 'ty';
        else if (rest.startsWith('(')) kind = 'fn';
      }
      tokens.push({ text, kind });
    }
    return tokens;
  });
}

function nextStop(lines, typed) {
  let offset = 0;
  for (const line of lines) {
    const length = line.reduce((sum, token) => sum + token.text.length, 0);
    if (typed < offset + length + 1) {
      const local = typed - offset;
      const indent = line.length > 0 && line[0].kind === '' && /^\s+$/.test(line[0].text) ? line[0].text.length : 0;
      return local < indent ? offset + indent : typed + 1;
    }
    offset += length + 1;
  }
  return typed + 1;
}

function renderLine(tokens, budget) {
  const parts = [];
  let remaining = budget;
  for (let index = 0; index < tokens.length && remaining > 0; index += 1) {
    const token = tokens[index];
    const text = token.text.slice(0, remaining);
    remaining -= text.length;
    parts.push(
      <span key={index} className={token.kind ? `code-showcase__${token.kind}` : undefined}>
        {text}
      </span>
    );
  }
  return parts;
}

export function CodeShowcase() {
  const t = useStrings();
  const cardRef = useRef(null);
  const reduced = useMemo(() => window.matchMedia('(prefers-reduced-motion: reduce)').matches, []);
  const [snippetIndex, setSnippetIndex] = useState(0);
  const [phase, setPhase] = useState(reduced ? 'accepted' : 'typing');
  const snippet = SNIPPETS[snippetIndex];
  const lines = useMemo(() => tokenize(snippet.code, snippet.language), [snippet]);
  const total = snippet.code.length;
  const [typed, setTyped] = useState(reduced ? total : 0);

  useEffect(() => {
    if (reduced) return undefined;
    if (phase === 'typing') {
      const timer = setInterval(() => {
        setTyped((current) => {
          const next = Math.min(total, nextStop(lines, current));
          return next;
        });
      }, TYPE_INTERVAL_MS);
      return () => clearInterval(timer);
    }
    const durations = { judging: JUDGE_MS, accepted: ACCEPTED_MS, clearing: CLEAR_MS };
    const timer = setTimeout(() => {
      if (phase === 'judging') setPhase('accepted');
      else if (phase === 'accepted') setPhase('clearing');
      else {
        setSnippetIndex((index) => (index + 1) % SNIPPETS.length);
        setTyped(0);
        setPhase('typing');
      }
    }, durations[phase]);
    return () => clearTimeout(timer);
  }, [phase, lines, total, reduced]);

  useEffect(() => {
    if (phase === 'typing' && typed >= total) setPhase('judging');
  }, [phase, typed, total]);

  useEffect(() => {
    const card = cardRef.current;
    if (!card || reduced || !window.matchMedia('(hover: hover) and (pointer: fine)').matches) return undefined;
    let frame = 0;
    const onMove = (event) => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const x = event.clientX / window.innerWidth - 0.5;
        const y = event.clientY / window.innerHeight - 0.5;
        card.style.setProperty('--tilt-x', `${(-y * 8).toFixed(2)}deg`);
        card.style.setProperty('--tilt-y', `${(x * 10).toFixed(2)}deg`);
      });
    };
    window.addEventListener('pointermove', onMove, { passive: true });
    return () => {
      window.removeEventListener('pointermove', onMove);
      cancelAnimationFrame(frame);
    };
  }, [reduced]);

  let consumed = 0;
  const visibleLines = lines.map((tokens) => {
    const length = tokens.reduce((sum, token) => sum + token.text.length, 0);
    const budget = Math.max(0, Math.min(length, typed - consumed));
    const started = typed >= consumed && (typed > consumed || consumed === 0);
    const lineState = { tokens, budget, started, active: typed >= consumed && typed <= consumed + length };
    consumed += length + 1;
    return lineState;
  });

  return (
    <div className="code-showcase-stage" aria-hidden="true">
      <div ref={cardRef} className={cx('code-showcase', `is-${phase}`)}>
        <div className="code-showcase__bar">
          <span className="code-showcase__tab">
            <span className="code-showcase__tab-dot" />
            {snippet.file}
          </span>
          <span className="code-showcase__language">{t(`languages.${snippet.language}`, { fallback: snippet.language })}</span>
        </div>
        <div className="code-showcase__code" style={{ '--lines': MAX_LINES }}>
          {visibleLines.map((line, index) =>
            line.started ? (
              <div key={`${snippetIndex}-${index}`} className="code-showcase__line">
                <span className="code-showcase__number">{index + 1}</span>
                <span className="code-showcase__text">
                  {renderLine(line.tokens, line.budget)}
                  {phase === 'typing' && line.active && <span className="code-showcase__caret" />}
                </span>
              </div>
            ) : null
          )}
        </div>
        <div className="code-showcase__status">
          {phase === 'typing' && <span className="code-showcase__muted">{t('auth.showcase.writing')}</span>}
          {phase === 'judging' && (
            <span className="code-showcase__judging">
              <span className="spinner" />
              {t('workspace.console.judgingHidden')}
            </span>
          )}
          {(phase === 'accepted' || phase === 'clearing') && (
            <span className="code-showcase__verdict">
              <LuCircleCheck />
              <strong>{t('verdicts.Accepted')}</strong>
              <span>{t('workspace.console.hiddenAccepted')}</span>
            </span>
          )}
          <span className="code-showcase__progress" />
        </div>
      </div>
    </div>
  );
}
